// api.ts - typed REST client for the PDS backend.
// Wire format is snake_case; the app types are camelCase. This module owns the
// translation in both directions so the rest of the app only sees camelCase.
//
// 도메인 엔드포인트(개발품/일정/마일스톤/지연·이슈/마스터)는 이 파일 하단에
// `request` / `fileRequest` / `uploadFile` 위에 얹혀 있다. fetch 패턴은 하나뿐이다.
import {
  type ErpFile,
  type ErpMeta,
  type ErpOption,
  type ErpRow,
  type ErpViewResult,
  type Wire,
  toMeta,
  toResult,
  viewQuery,
} from "@/lib/erp";
import { firebaseAuth } from "@/lib/firebase";
import type { LeadTimeOffset, MilestoneKey, StatusCode } from "@/lib/status";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";

export class ApiError extends Error {
  status: number;
  /** 서버 `detail` 원본. 422 필드별 오류(`{errors: {field: msg}}`)처럼 문자열이 아닐 수 있다. */
  detail: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

/** `detail` 이 문자열이면 그대로, 객체면 사람이 읽을 요약으로 (`[object Object]` 방지). */
function detailMessage(detail: unknown, fallback: string): string {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "errors" in detail) {
    const errors = (detail as { errors: Record<string, unknown> }).errors;
    const first = Object.values(errors)[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") return String(Object.values(first)[0] ?? fallback);
  }
  return fallback;
}

// 슈퍼어드민 임퍼소네이션 - SUPERADMIN_KEY 로 서명된 토큰을 bearer 로 쓰면 대상
// 유저로 완전 로그인(쓰기 포함)된다. localStorage 에 저장해 새로고침(그리고
// Playwright 네비게이션)에도 유지한다.
const IMP_KEY = "jwpds_impersonation";
export interface Impersonation {
  token: string;
  uid: string;
  email: string;
  name: string;
}

export function getImpersonation(): Impersonation | null {
  try {
    const raw = localStorage.getItem(IMP_KEY);
    return raw ? (JSON.parse(raw) as Impersonation) : null;
  } catch {
    return null;
  }
}

export function setImpersonation(imp: Impersonation | null): void {
  if (imp) localStorage.setItem(IMP_KEY, JSON.stringify(imp));
  else localStorage.removeItem(IMP_KEY);
}

// 임퍼소네이션 중이면 그 토큰, 아니면 Firebase ID 토큰을 bearer 로 쓴다.
async function authToken(): Promise<string | null> {
  const imp = getImpersonation();
  if (imp) return imp.token;
  return firebaseAuth.getIdToken();
}

// 임퍼소네이션 토큰 만료(401) → 세션 정리 후 새로고침해 원래 계정으로 복귀
// (만료 토큰이 UI 를 영구히 막지 않도록). 리로드 루프는 imp 존재 여부로 방지.
function bounceIfImpersonationExpired(statusCode: number): void {
  if (statusCode === 401 && getImpersonation()) {
    setImpersonation(null);
    window.location.reload();
  }
}

// Core request helper: injects the Firebase ID token when a user is signed in
// (the backend allows anonymous calls in dev/CI), sets JSON headers, and throws
// a typed ApiError on non-2xx.
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await authToken();
  const headers = new Headers(init.headers);
  if (init.body != null) headers.set("Content-Type", "application/json");
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${BASE}/api${path}`, { ...init, headers });

  if (!res.ok) {
    bounceIfImpersonationExpired(res.status);
    let message = res.statusText;
    let detail: unknown;
    try {
      const errBody = (await res.json()) as { detail?: unknown; message?: string };
      detail = errBody?.detail;
      message = detailMessage(detail, errBody?.message ?? message);
    } catch {
      // non-JSON error body - keep statusText
    }
    throw new ApiError(res.status, message, detail);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// 파일 계열은 JSON 전용 request 를 쓰지 않는다 - FormData 는 브라우저가 boundary
// 포함 Content-Type 을 직접 설정해야 하고, 응답이 바이너리다.
export async function fileRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await authToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${BASE}/api${path}`, { ...init, headers });
  if (!res.ok) {
    bounceIfImpersonationExpired(res.status);
    let message = res.statusText;
    let detail: unknown;
    try {
      const errBody = (await res.json()) as { detail?: unknown };
      detail = errBody?.detail;
      message = detailMessage(detail, message);
    } catch {
      // non-JSON error body
    }
    throw new ApiError(res.status, message, detail);
  }
  return res;
}

export async function uploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const res = await fileRequest(path, { method: "POST", body: form });
  return (await res.json()) as T;
}

// 파일은 인증 헤더가 필요해 <img src>/<a href> 직접 사용 불가 - blob URL 로 변환한다.
// 호출부가 URL.revokeObjectURL 로 해제할 책임을 진다.
// key 형식: <prefix>/<uuid>/<filename> - 백엔드 라우트가 {key:path} 이므로
// / 자체는 보존하고 각 세그먼트만 인코딩한다.
export async function fetchFileUrl(basePath: string, key: string): Promise<string> {
  const encodedKey = key.split("/").map(encodeURIComponent).join("/");
  const res = await fileRequest(`${basePath}/${encodedKey}`);
  return URL.createObjectURL(await res.blob());
}

// ----- 사용자(/me) -----------------------------------------------------------

export type UserRole = "admin" | "member";

export interface Me {
  uid: string;
  email: string;
  name: string;
  role: UserRole;
  position: string;
  dept: string;
  companyId: string;
  companyName: string;
  /** null 이면 §14 개인화면이 "담당자 정보가 연결되지 않았습니다" 를 띄운다 */
  staffId: string | null;
  staffName: string;
  imagesEnabled: boolean;
  isSuperadmin: boolean;
}

interface MeWire {
  uid: string;
  email: string;
  name: string;
  role: UserRole;
  position: string;
  dept: string;
  company_id: string;
  company_name: string;
  staff_id: string | null;
  staff_name: string;
  images_enabled: boolean;
  is_superadmin: boolean;
}

const meFromWire = (w: MeWire): Me => ({
  uid: w.uid,
  email: w.email,
  name: w.name,
  role: w.role,
  position: w.position,
  dept: w.dept,
  companyId: w.company_id ?? "",
  companyName: w.company_name ?? "",
  staffId: w.staff_id ?? null,
  staffName: w.staff_name ?? "",
  imagesEnabled: w.images_enabled,
  isSuperadmin: w.is_superadmin,
});

export async function getMe(): Promise<Me> {
  return meFromWire(await request<MeWire>("/me"));
}

export async function updateMe(patch: {
  name?: string;
  position?: string;
  dept?: string;
}): Promise<Me> {
  return meFromWire(await request<MeWire>("/me", { method: "PATCH", body: JSON.stringify(patch) }));
}

// ----- Superadmin ------------------------------------------------------------
// 운영 콘솔 전용 raw 조회 - wire(snake_case) 그대로 노출한다(원본 데이터 뷰어).

export interface SuperadminUser {
  uid: string;
  email: string;
  name: string;
  role: UserRole;
  position: string;
  dept: string;
  counts: Record<string, number>;
}

export interface SuperadminCollectionPage {
  total: number;
  items: Record<string, unknown>[];
}

export async function getSuperadminUsers(): Promise<SuperadminUser[]> {
  return request<SuperadminUser[]>("/superadmin/users");
}

export async function getSuperadminCollection(
  uid: string,
  name: string,
  skip: number,
  limit: number
): Promise<SuperadminCollectionPage> {
  return request<SuperadminCollectionPage>(
    `/superadmin/users/${encodeURIComponent(uid)}/collections/${encodeURIComponent(name)}?skip=${skip}&limit=${limit}`
  );
}

// ----- 사용자 활동 지표 -------------------------------------------------------
// 집계원은 서버의 `(uid, date)` 요청 카운터다 (`api/repository_activity.py`).

export interface ActivityPoint {
  date: string;
  requests: number;
  active_users: number;
}

export interface ActivityUser {
  uid: string;
  email: string;
  name: string;
  role: UserRole;
  company_id: string;
  /** 마지막 접속 `"YYYY-MM-DD HH:MM"` (KST). 빈 문자열 = 기록 없음. */
  last_seen_at: string;
  active_days: number;
  requests: number;
}

export interface ActivityReport {
  days: number;
  since: string;
  until: string;
  total_requests: number;
  active_users: number;
  daily: ActivityPoint[];
  users: ActivityUser[];
}

export async function getSuperadminActivity(days: number): Promise<ActivityReport> {
  return request<ActivityReport>(`/superadmin/activity?days=${days}`);
}

export interface ProvisionAccountInput {
  email: string;
  password: string;
  name?: string;
  role?: UserRole;
  /** 이 계정의 회사에 데모 데이터를 1회 심는다 (DEMO_SEED 자동 시드와 무관). */
  seed_demo?: boolean;
}

export interface ProvisionedAccount {
  uid: string;
  email: string;
  name: string;
  role: UserRole;
  company_id: string;
  staff_id: string | null;
  created: boolean;
  seeded: boolean;
}

// 이메일 / 비밀번호 계정 생성(운영 콘솔). 서버 게이트는 SUPERADMIN_KEY 또는
// 슈퍼어드민 세션 - 웹에서는 후자로 통과한다.
export async function provisionAccount(input: ProvisionAccountInput): Promise<ProvisionedAccount> {
  return request<ProvisionedAccount>("/superadmin/accounts", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export interface ImpersonationTokenResponse extends Impersonation {
  expires_in: number;
}

// 슈퍼어드민 세션으로 대상 유저의 임퍼소네이션 토큰 발급(웹 "이 사용자로 로그인").
export async function mintImpersonationToken(uid: string): Promise<ImpersonationTokenResponse> {
  return request<ImpersonationTokenResponse>("/superadmin/impersonation-token", {
    method: "POST",
    body: JSON.stringify({ uid }),
  });
}

/* =====================================================================
   도메인 타입 (camelCase) + 와이어 매퍼
   와이어는 snake_case 다. 자동 변환을 쓰지 않고 필드마다 손으로 매핑한다
   (§05 §0-3). 매퍼가 있는 곳이 곧 계약을 읽은 곳이다.
   ===================================================================== */

/** 쿼리스트링. undefined / "" 는 아예 보내지 않는다 (서버 기본값을 살린다). */
function qs(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

/** PATCH 본문. 변경된 필드만 보낸다 (서버 `exclude_unset` 과 대응, §05 §13-1). */
function patchBody(entries: Record<string, unknown>): string {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(entries)) {
    if (v !== undefined) out[k] = v;
  }
  return JSON.stringify(out);
}

export interface StageRef {
  key: MilestoneKey;
  label: string;
}
export interface ProcessRef {
  id: string;
  name: string;
}

/* ── Dashboard (§05 §3) ──────────────────────────────────────────── */

export interface DashboardKpi {
  total: number;
  green: number;
  yellow: number;
  red: number;
  thisWeekProduction: number;
  sopImminent: number;
  gray: number;
  blue: number;
}

export type AlertCode =
  | "delayed"
  | "this_week"
  | "sop_imminent"
  | "prod_wo_material"
  | "prod_wo_tooling";

export interface AlertCard {
  code: AlertCode;
  /** "red" | "yellow" | "warn" - 화면이 글리프를 고르는 힌트 */
  icon: string;
  label: string;
  count: number;
}

export interface DashboardSummary {
  asOf: string;
  kpi: DashboardKpi;
  alerts: AlertCard[];
  settings: { imminentDays: number; sopImminentDays: number };
}

interface DashboardSummaryWire {
  as_of: string;
  kpi: {
    total: number;
    green: number;
    yellow: number;
    red: number;
    this_week_production: number;
    sop_imminent: number;
    gray: number;
    blue: number;
  };
  alerts: { code: AlertCode; icon: string; label: string; count: number }[];
  settings: { imminent_days: number; sop_imminent_days: number };
}

const summaryFromWire = (w: DashboardSummaryWire): DashboardSummary => ({
  asOf: w.as_of,
  kpi: {
    total: w.kpi.total,
    green: w.kpi.green,
    yellow: w.kpi.yellow,
    red: w.kpi.red,
    thisWeekProduction: w.kpi.this_week_production,
    sopImminent: w.kpi.sop_imminent,
    gray: w.kpi.gray,
    blue: w.kpi.blue,
  },
  alerts: w.alerts.map((a) => ({ ...a })),
  settings: {
    imminentDays: w.settings.imminent_days,
    sopImminentDays: w.settings.sop_imminent_days,
  },
});

export interface DashboardItem {
  id: string;
  customerId: string;
  customerName: string;
  partNo: string;
  partName: string;
  projectName: string;
  staffId: string;
  staffName: string;
  sopDate: string;
  dDay: number | null;
  sopImminent: boolean;
  currentStage: StageRef | null;
  currentProcess: ProcessRef | null;
  /** §4-2 6~10번 컬럼 5개 키만 (§05 §3-2 계약 1) */
  stageStatus: Partial<Record<MilestoneKey, StatusCode>>;
  progress: number;
  status: StatusCode;
  archived: boolean;
}

interface DashboardItemWire {
  id: string;
  customer_id: string;
  customer_name: string;
  part_no: string;
  part_name: string;
  project_name: string;
  staff_id: string;
  staff_name: string;
  sop_date: string;
  d_day: number | null;
  sop_imminent: boolean;
  current_stage: StageRef | null;
  current_process: ProcessRef | null;
  stage_status: Partial<Record<MilestoneKey, StatusCode>>;
  progress: number;
  status: StatusCode;
  archived: boolean;
}

const dashboardItemFromWire = (w: DashboardItemWire): DashboardItem => ({
  id: w.id,
  customerId: w.customer_id,
  customerName: w.customer_name,
  partNo: w.part_no,
  partName: w.part_name,
  projectName: w.project_name,
  staffId: w.staff_id,
  staffName: w.staff_name,
  sopDate: w.sop_date,
  dDay: w.d_day,
  sopImminent: w.sop_imminent,
  currentStage: w.current_stage,
  currentProcess: w.current_process,
  stageStatus: w.stage_status ?? {},
  progress: w.progress,
  status: w.status,
  archived: w.archived,
});

export interface DashboardPage {
  total: number;
  items: DashboardItem[];
}

export interface DashboardFilters {
  customerId?: string;
  staffId?: string;
  stage?: string;
  /** 다중 상태. "red,yellow" */
  status?: string;
  q?: string;
  includeArchived?: boolean;
  /** SOP 임박 행만 (REQ-DASH-022). 서버가 걸러야 페이지네이션과 어긋나지 않는다. */
  sopImminent?: boolean;
}

export type DashboardSort =
  | "default"
  | "customer"
  | "part_name"
  | "staff"
  | "sop"
  | "progress"
  | "status";

const filterQuery = (
  f: DashboardFilters
): Record<string, string | number | boolean | undefined> => ({
  customer_id: f.customerId,
  staff_id: f.staffId,
  stage: f.stage,
  status: f.status,
  q: f.q,
  include_archived: f.includeArchived ? true : undefined,
  sop_imminent: f.sopImminent ? true : undefined,
});

export async function getDashboardSummary(f: DashboardFilters = {}): Promise<DashboardSummary> {
  return summaryFromWire(
    await request<DashboardSummaryWire>(`/dashboard/summary${qs(filterQuery(f))}`)
  );
}

export async function getDashboardItems(
  f: DashboardFilters = {},
  sort: DashboardSort = "default",
  order: "asc" | "desc" = "asc",
  skip = 0,
  limit = 200
): Promise<DashboardPage> {
  const w = await request<{ total: number; items: DashboardItemWire[] }>(
    `/dashboard/items${qs({ ...filterQuery(f), sort, order, skip, limit })}`
  );
  return { total: w.total, items: w.items.map(dashboardItemFromWire) };
}

/* ── 개발품 (§05 §4) ─────────────────────────────────────────────── */

export interface Drawing {
  key: string;
  filename: string;
  size: number;
  uploadedAt: string;
}

interface DrawingWire {
  key: string;
  filename: string;
  size: number;
  uploaded_at: string;
}

const drawingFromWire = (w: DrawingWire): Drawing => ({
  key: w.key,
  filename: w.filename,
  size: w.size,
  uploadedAt: w.uploaded_at,
});

export interface Milestone {
  id: string;
  key: MilestoneKey;
  seq: number;
  label: string;
  plannedDate: string;
  completedDate: string;
  status: StatusCode;
  isActive: boolean;
  autoGenerated: boolean;
  note: string;
}

interface MilestoneWire {
  id: string;
  key: MilestoneKey;
  seq: number;
  label: string;
  planned_date: string;
  completed_date: string;
  status: StatusCode;
  is_active: boolean;
  auto_generated: boolean;
  note: string;
}

const milestoneFromWire = (w: MilestoneWire): Milestone => ({
  id: w.id,
  key: w.key,
  seq: w.seq,
  label: w.label,
  plannedDate: w.planned_date,
  completedDate: w.completed_date,
  status: w.status,
  isActive: w.is_active,
  autoGenerated: w.auto_generated,
  note: w.note,
});

export interface PrepItem {
  id: string;
  kind: string;
  label: string;
  seq: number;
  requiredDate: string;
  expectedDate: string;
  completedDate: string;
  status: StatusCode;
  warning: string;
  note: string;
  linkedMilestone: string;
}

interface PrepItemWire {
  id: string;
  kind: string;
  label: string;
  seq: number;
  required_date: string;
  expected_date: string;
  completed_date: string;
  status: StatusCode;
  warning: string;
  note: string;
  linked_milestone?: string;
}

const prepItemFromWire = (w: PrepItemWire): PrepItem => ({
  id: w.id,
  kind: w.kind,
  label: w.label,
  seq: w.seq,
  requiredDate: w.required_date,
  expectedDate: w.expected_date,
  completedDate: w.completed_date,
  status: w.status,
  warning: w.warning,
  note: w.note,
  linkedMilestone: w.linked_milestone ?? "",
});

export interface RouteStep {
  processId: string;
  name: string;
  equipment: string;
}

interface RouteStepWire {
  process_id: string;
  name: string;
  equipment: string;
}

const routeStepFromWire = (w: RouteStepWire): RouteStep => ({
  processId: w.process_id,
  name: w.name,
  equipment: w.equipment,
});

export interface ProductionPlan {
  id: string;
  devItemId: string;
  partName: string;
  customerName: string;
  plannedDate: string;
  qty: number;
  processId: string;
  processName: string;
  equipment: string;
  staffId: string;
  staffName: string;
  isDone: boolean;
  doneDate: string;
  note: string;
  prepWarning: string;
}

interface ProductionPlanWire {
  id: string;
  dev_item_id: string;
  part_name: string;
  customer_name: string;
  planned_date: string;
  qty: number;
  process_id: string;
  process_name: string;
  equipment: string;
  staff_id: string;
  staff_name: string;
  is_done: boolean;
  done_date: string;
  note: string;
  prep_warning: string;
}

const productionPlanFromWire = (w: ProductionPlanWire): ProductionPlan => ({
  id: w.id,
  devItemId: w.dev_item_id,
  partName: w.part_name,
  customerName: w.customer_name,
  plannedDate: w.planned_date,
  qty: w.qty,
  processId: w.process_id,
  processName: w.process_name,
  equipment: w.equipment,
  staffId: w.staff_id,
  staffName: w.staff_name,
  isDone: w.is_done,
  doneDate: w.done_date,
  note: w.note,
  prepWarning: w.prep_warning,
});

export type IssueTargetType = "milestone" | "prep" | "production";

export interface Issue {
  id: string;
  targetType: IssueTargetType;
  targetId: string;
  targetLabel: string;
  plannedDate: string;
  expectedDate: string;
  delayDays: number;
  reason: string;
  action: string;
  status: "open" | "resolved";
  createdAt: string;
  updatedAt: string;
}

interface IssueWire {
  id: string;
  target_type: IssueTargetType;
  target_id: string;
  target_label: string;
  planned_date: string;
  expected_date: string;
  delay_days: number;
  reason: string;
  action: string;
  status: "open" | "resolved";
  created_at: string;
  updated_at: string;
}

const issueFromWire = (w: IssueWire): Issue => ({
  id: w.id,
  targetType: w.target_type,
  targetId: w.target_id,
  targetLabel: w.target_label,
  plannedDate: w.planned_date,
  expectedDate: w.expected_date,
  delayDays: w.delay_days,
  reason: w.reason,
  action: w.action,
  status: w.status,
  createdAt: w.created_at,
  updatedAt: w.updated_at,
});

export interface NextTask {
  date: string;
  label: string;
  kind: string;
  targetId: string;
}

export interface DevItem {
  id: string;
  customerId: string;
  customerName: string;
  projectName: string;
  partNo: string;
  partName: string;
  staffId: string;
  staffName: string;
  kickoffDate: string;
  sopDate: string;
  annualQty: number;
  note: string;
  /**
   * 저장값. 목록 API 는 순수 문자열 id 를, 상세/대시보드 API 는 `{id,name}` 을
   * 준다. 두 경로를 모두 흡수하고 화면에는 id 와 표시 이름을 따로 준다.
   */
  currentProcess: ProcessRef | null;
  currentProcessId: string;
  archived: boolean;
}

interface DevItemWire {
  id: string;
  customer_id: string;
  customer_name?: string;
  project_name: string;
  part_no: string;
  part_name: string;
  staff_id: string;
  staff_name?: string;
  kickoff_date: string;
  sop_date: string;
  annual_qty: number;
  note: string;
  current_process?: ProcessRef | string | null;
  archived: boolean;
}

const processRef = (v: ProcessRef | string | null | undefined): ProcessRef | null =>
  v && typeof v === "object" ? v : null;

const processId = (v: ProcessRef | string | null | undefined): string =>
  typeof v === "string" ? v : (v?.id ?? "");

const devItemFromWire = (w: DevItemWire): DevItem => ({
  id: w.id,
  customerId: w.customer_id,
  customerName: w.customer_name ?? "",
  projectName: w.project_name,
  partNo: w.part_no,
  partName: w.part_name,
  staffId: w.staff_id,
  staffName: w.staff_name ?? "",
  kickoffDate: w.kickoff_date,
  sopDate: w.sop_date,
  annualQty: w.annual_qty,
  note: w.note,
  currentProcess: processRef(w.current_process),
  currentProcessId: processId(w.current_process),
  archived: w.archived,
});

export interface DevItemDetail extends DevItem {
  asOf: string;
  progress: number;
  status: StatusCode;
  dDay: number | null;
  dDayLabel: string;
  sopForecast: string;
  currentStage: StageRef | null;
  nextTask: NextTask | null;
  drawings: Drawing[];
  milestones: Milestone[];
  prepItems: PrepItem[];
  route: RouteStep[];
  productionPlans: ProductionPlan[];
  issues: Issue[];
}

interface DevItemDetailWire extends DevItemWire {
  as_of: string;
  progress: number;
  status: StatusCode;
  d_day: number | null;
  d_day_label: string;
  sop_forecast: string;
  current_stage: StageRef | null;
  next_task: { date: string; label: string; kind: string; target_id: string } | null;
  drawings: DrawingWire[];
  milestones: MilestoneWire[];
  prep_items: PrepItemWire[];
  route: { steps: RouteStepWire[] } | null;
  production_plans: ProductionPlanWire[];
  issues: IssueWire[];
}

const devItemDetailFromWire = (w: DevItemDetailWire): DevItemDetail => ({
  ...devItemFromWire(w),
  asOf: w.as_of,
  progress: w.progress,
  status: w.status,
  dDay: w.d_day,
  dDayLabel: w.d_day_label,
  sopForecast: w.sop_forecast,
  currentStage: w.current_stage,
  nextTask: w.next_task
    ? {
        date: w.next_task.date,
        label: w.next_task.label,
        kind: w.next_task.kind,
        targetId: w.next_task.target_id,
      }
    : null,
  drawings: (w.drawings ?? []).map(drawingFromWire),
  milestones: (w.milestones ?? []).map(milestoneFromWire),
  prepItems: (w.prep_items ?? []).map(prepItemFromWire),
  route: (w.route?.steps ?? []).map(routeStepFromWire),
  productionPlans: (w.production_plans ?? []).map(productionPlanFromWire),
  issues: (w.issues ?? []).map(issueFromWire),
});

export interface DevItemInput {
  customerId: string;
  projectName?: string;
  partNo: string;
  partName: string;
  staffId: string;
  kickoffDate?: string;
  sopDate?: string;
  annualQty?: number;
  note?: string;
  routeProcessIds?: string[];
}

export async function listDevItems(
  f: { customerId?: string; staffId?: string; q?: string; includeArchived?: boolean } = {}
): Promise<DevItem[]> {
  const w = await request<DevItemWire[]>(
    `/dev-items${qs({
      customer_id: f.customerId,
      staff_id: f.staffId,
      q: f.q,
      include_archived: f.includeArchived ? true : undefined,
    })}`
  );
  return w.map(devItemFromWire);
}

export async function getDevItem(id: string): Promise<DevItemDetail> {
  return devItemDetailFromWire(
    await request<DevItemDetailWire>(`/dev-items/${encodeURIComponent(id)}`)
  );
}

export async function createDevItem(input: DevItemInput): Promise<DevItemDetail> {
  return devItemDetailFromWire(
    await request<DevItemDetailWire>("/dev-items", {
      method: "POST",
      body: JSON.stringify({
        customer_id: input.customerId,
        project_name: input.projectName ?? "",
        part_no: input.partNo,
        part_name: input.partName,
        staff_id: input.staffId,
        kickoff_date: input.kickoffDate ?? "",
        sop_date: input.sopDate ?? "",
        annual_qty: input.annualQty ?? 0,
        note: input.note ?? "",
        route_process_ids: input.routeProcessIds ?? [],
      }),
    })
  );
}

export interface DevItemPatch {
  customerId?: string;
  projectName?: string;
  partNo?: string;
  partName?: string;
  staffId?: string;
  kickoffDate?: string;
  sopDate?: string;
  annualQty?: number;
  note?: string;
  /** 공정 마스터 id. Route 밖의 값이면 400 (REQ-PROC-005) */
  currentProcess?: string;
  archived?: boolean;
}

export async function updateDevItem(id: string, patch: DevItemPatch): Promise<DevItemDetail> {
  return devItemDetailFromWire(
    await request<DevItemDetailWire>(`/dev-items/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patchBody({
        customer_id: patch.customerId,
        project_name: patch.projectName,
        part_no: patch.partNo,
        part_name: patch.partName,
        staff_id: patch.staffId,
        kickoff_date: patch.kickoffDate,
        sop_date: patch.sopDate,
        annual_qty: patch.annualQty,
        note: patch.note,
        current_process: patch.currentProcess,
        archived: patch.archived,
      }),
    })
  );
}

export async function deleteDevItem(id: string): Promise<void> {
  await request<void>(`/dev-items/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export interface ScheduleAdjustment {
  milestoneKey: string;
  rawDate: string;
  adjustedDate: string;
  reason: string;
}

export interface AutoScheduleResult {
  applied: number;
  skipped: number;
  adjustments: ScheduleAdjustment[];
  item: DevItemDetail;
}

/** §16 SOP 역산 자동생성. SOP 미등록이면 400 "SOP date is required". */
export async function autoScheduleDevItem(
  id: string,
  mode: "fill_empty" | "overwrite" = "fill_empty",
  templateId?: string
): Promise<AutoScheduleResult> {
  const w = await request<{
    applied: number;
    skipped: number;
    adjustments: {
      milestone_key: string;
      raw_date: string;
      adjusted_date: string;
      reason: string;
    }[];
    item: DevItemDetailWire;
  }>(`/dev-items/${encodeURIComponent(id)}/auto-schedule`, {
    method: "POST",
    body: JSON.stringify({ mode, template_id: templateId ?? null }),
  });
  return {
    applied: w.applied,
    skipped: w.skipped,
    adjustments: w.adjustments.map((a) => ({
      milestoneKey: a.milestone_key,
      rawDate: a.raw_date,
      adjustedDate: a.adjusted_date,
      reason: a.reason,
    })),
    item: devItemDetailFromWire(w.item),
  };
}

/* ── 도면 (§05 §4-7) ────────────────────────────────────────────── */

export const DRAWING_EXTENSIONS: readonly string[] = [
  "pdf",
  "dwg",
  "dxf",
  "step",
  "stp",
  "igs",
  "png",
  "jpg",
];
export const MAX_DRAWING_BYTES = 10 * 1024 * 1024;
export const MAX_DRAWINGS_PER_ITEM = 10;

export async function uploadDrawing(itemId: string, file: File): Promise<Drawing> {
  return drawingFromWire(
    await uploadFile<DrawingWire>(`/dev-items/${encodeURIComponent(itemId)}/drawings`, file)
  );
}

export async function deleteDrawing(itemId: string, key: string): Promise<void> {
  const encoded = key.split("/").map(encodeURIComponent).join("/");
  await request<void>(`/dev-items/${encodeURIComponent(itemId)}/drawings/${encoded}`, {
    method: "DELETE",
  });
}

/** presigned URL. 만료가 있으므로 클릭 시점에 받아 곧바로 쓴다. */
export async function getDrawingUrl(itemId: string, key: string): Promise<string> {
  const encoded = key.split("/").map(encodeURIComponent).join("/");
  const r = await request<{ url: string; expires_in: number }>(
    `/dev-items/${encodeURIComponent(itemId)}/drawings/${encoded}/url`
  );
  return r.url;
}

/* ── 마일스톤 (§05 §5) ──────────────────────────────────────────── */
// `GET /dev-items/{id}/milestones`, `.../prep-items`, `.../route` 의 클라이언트
// 래퍼는 두지 않는다. §05 A-2 대로 `GET /dev-items/{id}` 가 셋을 모두 담아
// 내려주므로 화면에 호출자가 생기지 않는다. 죽은 래퍼는 "여기서 조회한다"는
// 착각만 만든다. 필요해지면 그때 추가한다.

export async function updateMilestone(
  id: string,
  patch: { plannedDate?: string; completedDate?: string; note?: string }
): Promise<Milestone> {
  return milestoneFromWire(
    await request<MilestoneWire>(`/milestones/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patchBody({
        planned_date: patch.plannedDate,
        completed_date: patch.completedDate,
        note: patch.note,
      }),
    })
  );
}

/**
 * 완료 처리. **본문이 없다** - 완료일은 서버가 `today_kst()` 로 찍는다
 * (§05 A-4). 클라이언트가 날짜를 계산해 PATCH 하면 타임존 버그가 들어온다.
 */
export async function completeMilestone(id: string): Promise<Milestone> {
  return milestoneFromWire(
    await request<MilestoneWire>(`/milestones/${encodeURIComponent(id)}/complete`, {
      method: "POST",
    })
  );
}

export async function uncompleteMilestone(id: string): Promise<Milestone> {
  return milestoneFromWire(
    await request<MilestoneWire>(`/milestones/${encodeURIComponent(id)}/complete`, {
      method: "DELETE",
    })
  );
}

/* ── 공정 Route (§05 §6) ────────────────────────────────────────── */

/** 전체 교체(PUT). 순서가 의미를 가지는 배열은 부분 수정이 정의되지 않는다 (§05 A-8). */
export async function putRoute(
  itemId: string,
  steps: { processId: string; equipment?: string }[]
): Promise<RouteStep[]> {
  const w = await request<{ steps: RouteStepWire[] }>(
    `/dev-items/${encodeURIComponent(itemId)}/route`,
    {
      method: "PUT",
      body: JSON.stringify({
        steps: steps.map((s) => ({ process_id: s.processId, equipment: s.equipment ?? "" })),
      }),
    }
  );
  return (w.steps ?? []).map(routeStepFromWire);
}

/* ── 생산 준비현황 (§05 §7) ─────────────────────────────────────── */

export async function createPrepItem(
  itemId: string,
  input: { kind: string; label?: string; requiredDate?: string; expectedDate?: string }
): Promise<PrepItem> {
  return prepItemFromWire(
    await request<PrepItemWire>(`/dev-items/${encodeURIComponent(itemId)}/prep-items`, {
      method: "POST",
      body: JSON.stringify({
        kind: input.kind,
        label: input.label ?? "",
        required_date: input.requiredDate ?? "",
        expected_date: input.expectedDate ?? "",
      }),
    })
  );
}

export async function updatePrepItem(
  id: string,
  patch: { requiredDate?: string; expectedDate?: string; completedDate?: string; note?: string }
): Promise<PrepItem> {
  return prepItemFromWire(
    await request<PrepItemWire>(`/prep-items/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patchBody({
        required_date: patch.requiredDate,
        expected_date: patch.expectedDate,
        completed_date: patch.completedDate,
        note: patch.note,
      }),
    })
  );
}

export async function completePrepItem(id: string): Promise<PrepItem> {
  return prepItemFromWire(
    await request<PrepItemWire>(`/prep-items/${encodeURIComponent(id)}/complete`, {
      method: "POST",
    })
  );
}

export async function deletePrepItem(id: string): Promise<void> {
  await request<void>(`/prep-items/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/* ── 생산계획 (§05 §8) ──────────────────────────────────────────── */

export async function listProductionPlans(
  f: { devItemId?: string; staffId?: string; from?: string; to?: string; isDone?: boolean } = {}
): Promise<ProductionPlan[]> {
  const w = await request<ProductionPlanWire[]>(
    `/production-plans${qs({
      dev_item_id: f.devItemId,
      staff_id: f.staffId,
      from: f.from,
      to: f.to,
      is_done: f.isDone,
    })}`
  );
  return w.map(productionPlanFromWire);
}

export interface ProductionPlanInput {
  devItemId: string;
  plannedDate: string;
  qty?: number;
  processId?: string;
  equipment?: string;
  staffId?: string;
  note?: string;
}

export async function createProductionPlan(input: ProductionPlanInput): Promise<ProductionPlan> {
  return productionPlanFromWire(
    await request<ProductionPlanWire>("/production-plans", {
      method: "POST",
      body: JSON.stringify({
        dev_item_id: input.devItemId,
        planned_date: input.plannedDate,
        qty: input.qty ?? 0,
        process_id: input.processId ?? "",
        equipment: input.equipment ?? "",
        staff_id: input.staffId ?? "",
        note: input.note ?? "",
      }),
    })
  );
}

export async function updateProductionPlan(
  id: string,
  patch: {
    plannedDate?: string;
    qty?: number;
    processId?: string;
    equipment?: string;
    staffId?: string;
    isDone?: boolean;
    note?: string;
  }
): Promise<ProductionPlan> {
  return productionPlanFromWire(
    await request<ProductionPlanWire>(`/production-plans/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patchBody({
        planned_date: patch.plannedDate,
        qty: patch.qty,
        process_id: patch.processId,
        equipment: patch.equipment,
        staff_id: patch.staffId,
        is_done: patch.isDone,
        note: patch.note,
      }),
    })
  );
}

export async function completeProductionPlan(id: string): Promise<ProductionPlan> {
  return productionPlanFromWire(
    await request<ProductionPlanWire>(`/production-plans/${encodeURIComponent(id)}/done`, {
      method: "POST",
    })
  );
}

export async function deleteProductionPlan(id: string): Promise<void> {
  await request<void>(`/production-plans/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/* ── 지연 · 이슈 (§05 §9) ───────────────────────────────────────── */

export interface IssueRow {
  issueId: string | null;
  devItemId: string;
  customerName: string;
  partName: string;
  targetType: IssueTargetType;
  targetId: string;
  targetLabel: string;
  plannedDate: string;
  expectedDate: string;
  delayDays: number;
  reason: string;
  action: string;
  staffId: string;
  staffName: string;
  /** false = 사유 미입력 지연. POST 로 만들고, true 면 PATCH 한다 (§05 A-7) */
  hasIssueDoc: boolean;
  status: "open" | "resolved";
}

interface IssueRowWire {
  issue_id: string | null;
  dev_item_id: string;
  customer_name: string;
  part_name: string;
  target_type: IssueTargetType;
  target_id: string;
  target_label: string;
  planned_date: string;
  expected_date: string;
  delay_days: number;
  reason: string;
  action: string;
  staff_id: string;
  staff_name: string;
  has_issue_doc: boolean;
  status: "open" | "resolved";
}

const issueRowFromWire = (w: IssueRowWire): IssueRow => ({
  issueId: w.issue_id,
  devItemId: w.dev_item_id,
  customerName: w.customer_name,
  partName: w.part_name,
  targetType: w.target_type,
  targetId: w.target_id,
  targetLabel: w.target_label,
  plannedDate: w.planned_date,
  expectedDate: w.expected_date,
  delayDays: w.delay_days,
  reason: w.reason,
  action: w.action,
  staffId: w.staff_id,
  staffName: w.staff_name,
  hasIssueDoc: w.has_issue_doc,
  status: w.status,
});

export interface IssueFilters {
  customerId?: string;
  staffId?: string;
  targetType?: IssueTargetType | "";
  includeResolved?: boolean;
}

export async function listIssues(f: IssueFilters = {}): Promise<IssueRow[]> {
  const w = await request<IssueRowWire[]>(
    `/issues${qs({
      customer_id: f.customerId,
      staff_id: f.staffId,
      target_type: f.targetType,
      include_resolved: f.includeResolved ? true : undefined,
    })}`
  );
  return w.map(issueRowFromWire);
}

export interface IssueInput {
  devItemId: string;
  targetType: IssueTargetType;
  targetId: string;
  reason: string;
  action?: string;
  expectedDate?: string;
}

export async function createIssue(input: IssueInput): Promise<Issue> {
  return issueFromWire(
    await request<IssueWire>("/issues", {
      method: "POST",
      body: JSON.stringify({
        dev_item_id: input.devItemId,
        target_type: input.targetType,
        target_id: input.targetId,
        reason: input.reason,
        action: input.action ?? "",
        expected_date: input.expectedDate ?? "",
      }),
    })
  );
}

export async function updateIssue(
  id: string,
  patch: {
    reason?: string;
    action?: string;
    expectedDate?: string;
    status?: "open" | "resolved";
  }
): Promise<Issue> {
  return issueFromWire(
    await request<IssueWire>(`/issues/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patchBody({
        reason: patch.reason,
        action: patch.action,
        expected_date: patch.expectedDate,
        status: patch.status,
      }),
    })
  );
}

export async function deleteIssue(id: string): Promise<void> {
  await request<void>(`/issues/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/* ── Calendar (§05 §10) ─────────────────────────────────────────── */

export interface CalendarEvent {
  date: string;
  kind: "milestone" | "production";
  milestoneKey: MilestoneKey | null;
  eventLabel: string;
  devItemId: string;
  customerName: string;
  partName: string;
  staffName: string;
  status: StatusCode;
  targetId: string;
}

interface CalendarEventWire {
  date: string;
  kind: "milestone" | "production";
  milestone_key: MilestoneKey | null;
  event_label: string;
  dev_item_id: string;
  customer_name: string;
  part_name: string;
  staff_name: string;
  status: StatusCode;
  target_id: string;
}

const calendarEventFromWire = (w: CalendarEventWire): CalendarEvent => ({
  date: w.date,
  kind: w.kind,
  milestoneKey: w.milestone_key,
  eventLabel: w.event_label,
  devItemId: w.dev_item_id,
  customerName: w.customer_name,
  partName: w.part_name,
  staffName: w.staff_name,
  status: w.status,
  targetId: w.target_id,
});

export async function getCalendar(
  from: string,
  to: string,
  f: { customerId?: string; staffId?: string; includeProduction?: boolean } = {}
): Promise<CalendarEvent[]> {
  const w = await request<CalendarEventWire[]>(
    `/calendar${qs({
      from,
      to,
      customer_id: f.customerId,
      staff_id: f.staffId,
      include_production: f.includeProduction === false ? false : undefined,
    })}`
  );
  return w.map(calendarEventFromWire);
}

/* ── 내 업무 (§05 §11) ─────────────────────────────────────────── */

export interface MyTask {
  kind: "milestone" | "prep" | "production";
  targetId: string;
  devItemId: string;
  customerName: string;
  partName: string;
  taskLabel: string;
  plannedDate: string;
  status: StatusCode;
  delayDays: number;
  hasIssueDoc: boolean;
}

interface MyTaskWire {
  kind: "milestone" | "prep" | "production";
  target_id: string;
  dev_item_id: string;
  customer_name: string;
  part_name: string;
  task_label: string;
  planned_date: string;
  status: StatusCode;
  delay_days?: number;
  has_issue_doc?: boolean;
}

const myTaskFromWire = (w: MyTaskWire): MyTask => ({
  kind: w.kind,
  targetId: w.target_id,
  devItemId: w.dev_item_id,
  customerName: w.customer_name,
  partName: w.part_name,
  taskLabel: w.task_label,
  plannedDate: w.planned_date,
  status: w.status,
  delayDays: w.delay_days ?? 0,
  hasIssueDoc: w.has_issue_doc ?? false,
});

export interface MyItem {
  id: string;
  customerName: string;
  partName: string;
  sopDate: string;
  currentStage: StageRef | null;
  progress: number;
  status: StatusCode;
}

export interface MyTasks {
  asOf: string;
  staffId: string;
  staffName: string;
  staffLinked: boolean;
  delayed: MyTask[];
  todayTasks: MyTask[];
  weekTasks: MyTask[];
  myItems: MyItem[];
}

interface MyTasksWire {
  as_of: string;
  staff_id: string;
  staff_name: string;
  staff_linked: boolean;
  delayed: MyTaskWire[];
  today_tasks: MyTaskWire[];
  week_tasks: MyTaskWire[];
  my_items: {
    id: string;
    customer_name: string;
    part_name: string;
    sop_date: string;
    current_stage: StageRef | null;
    progress: number;
    status: StatusCode;
  }[];
}

export async function getMyTasks(staffId?: string): Promise<MyTasks> {
  const w = await request<MyTasksWire>(`/me/tasks${qs({ staff_id: staffId })}`);
  return {
    asOf: w.as_of,
    staffId: w.staff_id,
    staffName: w.staff_name,
    staffLinked: w.staff_linked,
    delayed: w.delayed.map(myTaskFromWire),
    todayTasks: w.today_tasks.map(myTaskFromWire),
    weekTasks: w.week_tasks.map(myTaskFromWire),
    myItems: w.my_items.map((i) => ({
      id: i.id,
      customerName: i.customer_name,
      partName: i.part_name,
      sopDate: i.sop_date,
      currentStage: i.current_stage,
      progress: i.progress,
      status: i.status,
    })),
  };
}

/* ── 마스터 (§05 §12) ──────────────────────────────────────────── */
// 아래 4종은 와이어가 이미 camel-free 라 매퍼 없이 직통이다 (§05 §13-1).

export interface Staff {
  id: string;
  name: string;
  dept: string;
  position: string;
  email: string;
  phone: string;
  active: boolean;
  /** GET 응답 전용 파생값 (마스터 화면의 `참조` 컬럼) */
  ref_count?: number;
}

export interface Customer {
  id: string;
  name: string;
  code: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  note: string;
  active: boolean;
  ref_count?: number;
}

export interface ProcessMaster {
  id: string;
  name: string;
  seq: number;
  active: boolean;
  ref_count?: number;
}

export interface Holiday {
  id: string;
  date: string;
  name: string;
}

/**
 * 마스터 POST 는 서버가 id 를 만들어주지 않는다(`XCreate.id` 필수).
 * 개발품과 달리 클라이언트가 채워야 하므로 여기서 한 곳에서만 만든다.
 *
 * 이건 **불투명한 새 식별자**다. 도메인 id 를 조립하는 것이 아니다 -
 * `{itemId}:{key}` 같은 서버 스킴을 프론트가 만드는 일은 없다 (F-10).
 */
export function newId(): string {
  const c = globalThis.crypto;
  if (c && "randomUUID" in c) return c.randomUUID().replace(/-/g, "");
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

const masterCrud = <T extends { id: string }>(res: string) => ({
  list: (activeOnly = false): Promise<T[]> =>
    request<T[]>(`/${res}${qs({ active_only: activeOnly || undefined })}`),
  create: (item: Omit<T, "id"> & { id?: string }): Promise<T> =>
    request<T>(`/${res}`, {
      method: "POST",
      body: JSON.stringify({ ...item, id: item.id || newId() }),
    }),
  update: (id: string, patch: Partial<T>): Promise<T> =>
    request<T>(`/${res}/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patchBody(patch as Record<string, unknown>),
    }),
  remove: (id: string): Promise<void> =>
    request<void>(`/${res}/${encodeURIComponent(id)}`, { method: "DELETE" }),
});

export const staffApi = masterCrud<Staff>("staff");
export const customersApi = masterCrud<Customer>("customers");
export const processesApi = masterCrud<ProcessMaster>("processes");

export const holidaysApi = {
  list: (year?: number): Promise<Holiday[]> => request<Holiday[]>(`/holidays${qs({ year })}`),
  // id 를 보내지 않는다. 서버가 정한다 - 프론트에서 id 를 조립하면 스킴이
  // 바뀔 때 조용히 깨진다 (불일치 F-10).
  create: (item: { date: string; name?: string }): Promise<Holiday> =>
    request<Holiday>("/holidays", {
      method: "POST",
      body: JSON.stringify({ date: item.date, name: item.name ?? "" }),
    }),
  remove: (id: string): Promise<void> =>
    request<void>(`/holidays/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export interface LeadTimeTemplate {
  id: string;
  name: string;
  isDefault: boolean;
  offsets: LeadTimeOffset[];
  prepOffsets: Record<string, number>;
}

interface LeadTimeTemplateWire {
  id: string;
  name: string;
  is_default: boolean;
  offsets: { milestone_key: string; offset_days: number; label?: string }[];
  prep_offsets: Record<string, number>;
}

const templateFromWire = (w: LeadTimeTemplateWire): LeadTimeTemplate => ({
  id: w.id,
  name: w.name,
  isDefault: w.is_default,
  offsets: (w.offsets ?? []).map((o) => ({
    milestoneKey: o.milestone_key,
    offsetDays: o.offset_days,
    label: o.label,
  })),
  prepOffsets: w.prep_offsets ?? {},
});

const templateToWire = (t: Partial<LeadTimeTemplate>): Record<string, unknown> =>
  patchBodyObject({
    name: t.name,
    is_default: t.isDefault,
    offsets: t.offsets?.map((o) => ({
      milestone_key: o.milestoneKey,
      offset_days: o.offsetDays,
      label: o.label ?? "",
    })),
    prep_offsets: t.prepOffsets,
  });

function patchBodyObject(entries: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(entries)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export const leadTimeApi = {
  list: (): Promise<LeadTimeTemplate[]> =>
    request<LeadTimeTemplateWire[]>("/lead-time-templates").then((w) => w.map(templateFromWire)),
  create: (t: Omit<LeadTimeTemplate, "id"> & { id?: string }): Promise<LeadTimeTemplate> =>
    request<LeadTimeTemplateWire>("/lead-time-templates", {
      method: "POST",
      body: JSON.stringify({ ...templateToWire(t), id: t.id || newId() }),
    }).then(templateFromWire),
  update: (id: string, patch: Partial<LeadTimeTemplate>): Promise<LeadTimeTemplate> =>
    request<LeadTimeTemplateWire>(`/lead-time-templates/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(templateToWire(patch)),
    }).then(templateFromWire),
  remove: (id: string): Promise<void> =>
    request<void>(`/lead-time-templates/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export interface MilestoneLabel {
  key: MilestoneKey;
  seq: number;
  label: string;
}

export const getMilestoneLabels = (): Promise<MilestoneLabel[]> =>
  request<MilestoneLabel[]>("/milestone-labels");

/** 9건 전체 교체. 개수나 key 집합이 다르면 400 (§05 §12-6). */
export const putMilestoneLabels = (labels: MilestoneLabel[]): Promise<MilestoneLabel[]> =>
  request<MilestoneLabel[]>("/milestone-labels", {
    method: "PUT",
    body: JSON.stringify(labels),
  });

export interface CompanySettings {
  imminentDays: number;
  sopImminentDays: number;
  prodPrepLookaheadDays: number;
  weekStart: string;
  businessDayAdjust: "backward" | "forward";
  /** 읽기 전용. PATCH 로 바꿀 수 없다 (§05 §12-8) */
  timezone: string;
  progressWeights: Record<MilestoneKey, number>;
}

interface CompanySettingsWire {
  imminent_days: number;
  sop_imminent_days: number;
  prod_prep_lookahead_days: number;
  week_start: string;
  business_day_adjust: "backward" | "forward";
  timezone: string;
  progress_weights: Record<MilestoneKey, number>;
}

const settingsFromWire = (w: CompanySettingsWire): CompanySettings => ({
  imminentDays: w.imminent_days,
  sopImminentDays: w.sop_imminent_days,
  prodPrepLookaheadDays: w.prod_prep_lookahead_days,
  weekStart: w.week_start,
  businessDayAdjust: w.business_day_adjust,
  timezone: w.timezone,
  progressWeights: w.progress_weights,
});

export const getSettings = (): Promise<CompanySettings> =>
  request<CompanySettingsWire>("/settings").then(settingsFromWire);

export const updateSettings = (patch: Partial<CompanySettings>): Promise<CompanySettings> =>
  request<CompanySettingsWire>("/settings", {
    method: "PATCH",
    body: patchBody({
      imminent_days: patch.imminentDays,
      sop_imminent_days: patch.sopImminentDays,
      prod_prep_lookahead_days: patch.prodPrepLookaheadDays,
      business_day_adjust: patch.businessDayAdjust,
      progress_weights: patch.progressWeights,
    }),
  }).then(settingsFromWire);

export interface AdminUser {
  uid: string;
  email: string;
  name: string;
  role: UserRole;
  staffId: string | null;
  position: string;
  dept: string;
}

interface AdminUserWire {
  uid: string;
  email: string;
  name: string;
  role: UserRole;
  staff_id: string | null;
  position: string;
  dept: string;
}

const adminUserFromWire = (w: AdminUserWire): AdminUser => ({
  uid: w.uid,
  email: w.email,
  name: w.name,
  role: w.role,
  staffId: w.staff_id,
  position: w.position,
  dept: w.dept,
});

export const listUsers = (): Promise<AdminUser[]> =>
  request<AdminUserWire[]>("/users").then((w) => w.map(adminUserFromWire));

export const updateUser = (
  uid: string,
  patch: { role?: UserRole; staffId?: string; name?: string; position?: string; dept?: string }
): Promise<AdminUser> =>
  request<AdminUserWire>(`/users/${encodeURIComponent(uid)}`, {
    method: "PATCH",
    body: patchBody({
      role: patch.role,
      staff_id: patch.staffId,
      name: patch.name,
      position: patch.position,
      dept: patch.dept,
    }),
  }).then(adminUserFromWire);

/* =====================================================================
   ERP 업무 (기존 ERP 이관) - 명세는 `lib/erp.ts`, 여기는 호출만
   ===================================================================== */

export const getErpMeta = async (): Promise<ErpMeta> => toMeta(await request<Wire>("/erp/meta"));

export const runErpView = async (
  key: string,
  params: Record<string, string>,
  offset = 0,
  limit = 0
): Promise<ErpViewResult> =>
  toResult(
    await request<Wire>(`/erp/views/${encodeURIComponent(key)}${viewQuery(params, offset, limit)}`)
  );

export const getErpOptions = (entity: string): Promise<ErpOption[]> =>
  request<ErpOption[]>(`/erp/options/${encodeURIComponent(entity)}`);

const erpRecordPath = (entity: string, id?: string) =>
  `/erp/records/${encodeURIComponent(entity)}${id ? `/${encodeURIComponent(id)}` : ""}`;

export const createErpRecord = (entity: string, body: ErpRow): Promise<ErpRow> =>
  request<ErpRow>(erpRecordPath(entity), { method: "POST", body: JSON.stringify(body) });

export const updateErpRecord = (entity: string, id: string, patch: ErpRow): Promise<ErpRow> =>
  request<ErpRow>(erpRecordPath(entity, id), { method: "PATCH", body: JSON.stringify(patch) });

export const deleteErpRecord = (entity: string, id: string): Promise<void> =>
  request<void>(erpRecordPath(entity, id), { method: "DELETE" });

const erpFilesPath = (entity: string, id: string, field: string) =>
  `${erpRecordPath(entity, id)}/files/${encodeURIComponent(field)}`;
const erpKey = (key: string) => key.split("/").map(encodeURIComponent).join("/");

export const uploadErpFile = async (
  entity: string,
  id: string,
  field: string,
  file: File
): Promise<ErpFile> => {
  const w = await uploadFile<Wire>(erpFilesPath(entity, id, field), file);
  return {
    key: String(w.key ?? ""),
    filename: String(w.filename ?? ""),
    size: Number(w.size ?? 0),
    uploadedAt: String(w.uploaded_at ?? ""),
  };
};

/** presigned URL (5분). 새 탭으로 연다. */
export const getErpFileUrl = async (
  entity: string,
  id: string,
  field: string,
  key: string
): Promise<string> =>
  (await request<{ url: string }>(`${erpFilesPath(entity, id, field)}/${erpKey(key)}/url`)).url;

export const deleteErpFile = (
  entity: string,
  id: string,
  field: string,
  key: string
): Promise<void> =>
  request<void>(`${erpFilesPath(entity, id, field)}/${erpKey(key)}`, { method: "DELETE" });
